import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Divider, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import EditIcon from "@mui/icons-material/Edit";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages, { getAssetNodeTypeLabel } from "../cmmsMessages";
import messages from "../messages";
import { cmmsTypeScale, getAssetHierarchyPalette, getContainedButtonSx, getOutlinedButtonSx, getPanelTitleSx, getSectionBoxSx, getSemanticButtonSx, getStatusChipSx, getTabsSx, getTypeChipSx } from "../assetHierarchyStyles";
import { useAuth } from "../../../auth/AuthContext";
import { HideIfNoRole, hasAnyRole } from "../../../auth/authUi";
import { CMMS_FIELD_ROLES, CMMS_MANAGER_ROLES } from "../cmmsRoles";
import { getMaxModelYear, isValidModelYear, MIN_MODEL_YEAR, parseModelYear } from "../assetHierarchyUtils";
import AssetDescriptionTab from "./AssetDescriptionTab";
import AssetDocumentList from "./AssetDocumentList";
import AssetMaintenanceTab from "./AssetMaintenanceTab";
import AssetOverviewImage from "./AssetOverviewImage";
import AssetUsageTab from "./AssetUsageTab";
import AssetWorkOrderHistoryTab from "./AssetWorkOrderHistoryTab";
import EditableDetailLine from "./EditableDetailLine";
import MachineAssignmentPanel from "./MachineAssignmentPanel";
import QuickReportDialog from "./QuickReportDialog";

// The nameplate text columns are nvarchar(100) (AxionOEEContext), the same limit the edit dialog
// puts on these fields.
const nameplateTextInputProps = { maxLength: 100 };
const modelYearInputProps = { inputMode: "numeric", maxLength: 4 };

export default function AssetNodeDetailsPanel({
  canAddChild,
  canDeactivate,
  companyKey,
  isUpdatingNode,
  linkedMachine,
  machineOptions,
  onAddChild,
  onAssignMachine,
  onDeactivate,
  onEdit,
  onMove,
  onUnassignMachine,
  onUpdateNode,
  selectedNode,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const { roleIds } = useAuth();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  // The in-place edits are the edit dialog's, one field at a time, so they have its gate: the
  // PATCH behind both is Cmms_Manager.
  const canEditNode = hasAnyRole(roleIds, CMMS_MANAGER_ROLES);
  const [reportOpen, setReportOpen] = useState(false);
  const [tab, setTab] = useState(0);

  // Reset to the first tab whenever a different node is selected, so the panel
  // never opens on a tab that belonged to the previously selected asset.
  const selectedNodeId = selectedNode?.id;
  useEffect(() => {
    setTab(0);
  }, [selectedNodeId]);

  if (!selectedNode) {
    return (
      <Box sx={getSectionBoxSx(cmmsColors)}>
        <Typography variant="h6" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
          <FormattedMessage {...messages.selectNodeTitle} />
        </Typography>
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14, mt: 1 }}>
          <FormattedMessage {...messages.selectNodeHint} />
        </Typography>
      </Box>
    );
  }

  const canMove = selectedNode.nodeType !== 1 && Boolean(selectedNode.parentAssetNodeId);
  // Only a maintenance object carries a nameplate; the API refuses one anywhere else.
  const hasMachineCard = selectedNode.nodeType === 3;
  const noneText = intl.formatMessage(messages.valueNone);
  const nameplateLineProps = { emptyText: noneText, canEdit: canEditNode, disabled: isUpdatingNode };
  // Operators report problems against maintenance objects (nodeType 3). Visible to all roles.
  const canReport = selectedNode.nodeType === 3 && selectedNode.isActive;

  // Keyed tab array so upcoming per-asset panels (documents list, tool service
  // status, runtime hours, work orders) are a single entry each. Only the active
  // tab's render() runs, which keeps each tab's data fetching lazy.
  const tabs = [
    {
      // First, so selecting a node opens on what it is. A tab of its own rather than a line in
      // the metadata because a description can run to paragraphs.
      key: "description",
      label: intl.formatMessage(messages.tabDescription),
      // Keyed by node so a half-written edit never carries over to the next node.
      render: () => (
        <AssetDescriptionTab
          key={selectedNode.id}
          description={selectedNode.description}
          disabled={isUpdatingNode}
          onSave={(description) => onUpdateNode({ description }, messages.toastDescriptionSaved)}
        />
      ),
    },
    {
      key: "documents",
      label: intl.formatMessage(messages.tabDocuments),
      render: () => (
        <AssetDocumentList
          assetNodeId={selectedNode.id}
          companyKey={companyKey}
          overviewImageDocumentId={selectedNode.overviewImageDocumentId}
        />
      ),
    },
    {
      key: "machine",
      label: intl.formatMessage(messages.tabMachine),
      render: () => (
        <MachineAssignmentPanel
          linkedMachine={linkedMachine}
          machineOptions={machineOptions}
          onAssignMachine={onAssignMachine}
          onUnassignMachine={onUnassignMachine}
          selectedNode={selectedNode}
        />
      ),
    },
    {
      key: "usage",
      label: intl.formatMessage(messages.tabUsage),
      render: () => (
        <AssetUsageTab companyKey={companyKey} assetNodeId={selectedNode.id} />
      ),
    },
    {
      key: "history",
      label: intl.formatMessage(messages.tabHistory),
      // Keyed by node so its paging starts over on every node, whether or not the tab
      // reset above has run yet.
      render: () => (
        <AssetWorkOrderHistoryTab key={selectedNode.id} companyKey={companyKey} assetNodeId={selectedNode.id} />
      ),
    },
    {
      key: "maintenance",
      label: intl.formatMessage(messages.tabMaintenance),
      // Keyed by node for the same reason as the history: its work orders are paged too.
      render: () => (
        <AssetMaintenanceTab key={selectedNode.id} companyKey={companyKey} assetNodeId={selectedNode.id} />
      ),
    },
  ];
  const activeTabIndex = Math.min(tab, tabs.length - 1);
  const activeTab = tabs[activeTabIndex];

  // Root-first and without the node itself; empty for a site. Joined the way the table's Path
  // column joins it, so a path reads the same in both views.
  const ancestorPath = (selectedNode.ancestorNames ?? []).join(" / ");

  /*
   * Name, where it sits, and chips. Every node has them; a maintenance object puts them inside its
   * machine card.
   *
   * The path stands in for the metadata list this panel used to carry. Of its fields, name and
   * type were already the title and the first chip, sort order only matters while editing (it is
   * in the edit dialog), and the parent was the one worth reading, which the full path says better.
   * It matters most where the tree is not beside the panel: in the table's drawer and after a
   * search.
   */
  const nodeHeading = (
    <>
      <Typography variant={cmmsTypeScale.panelTitle} sx={{ ...getPanelTitleSx(cmmsColors), wordBreak: "break-word" }}>
        {selectedNode.name}
      </Typography>

      {ancestorPath &&
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, mt: 0.5, wordBreak: "break-word" }}>
          {ancestorPath}
        </Typography>
      }

      {/*
        * The chips on a row of their own rather than opposite the name. In the drawer and in
        * the tree tab's narrow column a long node name pushed them out; this is the shape
        * WorkOrderDetailsPanel already uses for its status, priority and type badges.
        */}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
        <Chip label={getAssetNodeTypeLabel(selectedNode.nodeType, intl)} size="small" sx={getTypeChipSx(cmmsColors)} />
        <Chip
          label={intl.formatMessage(selectedNode.isActive ? messages.statusActive : messages.statusInactive)}
          size="small"
          sx={getStatusChipSx(cmmsColors, selectedNode.isActive)}
        />
      </Stack>
    </>
  );

  // Spaced like the machine page's infoboxes, OEE's gap between tiles.
  return (
    <Stack spacing={1.25}>
      {/*
        * A maintenance object's heading and its machine card are one tile: the photo on the left,
        * and beside it the name, the chips and the nameplate, the way a printed machine card reads.
        * The intro line gives way to the nameplate, which says more. On a phone the name comes
        * first, so the card does not open on a picture with no caption.
        */}
      {hasMachineCard &&
        <Box sx={getSectionBoxSx(cmmsColors)}>
          <Box
            sx={{
              display: "grid",
              columnGap: 2.5,
              rowGap: 2,
              alignItems: "start",
              gridTemplateColumns: { xs: "1fr", sm: "240px minmax(0, 1fr)" },
              gridTemplateRows: { sm: "auto 1fr" },
              gridTemplateAreas: {
                xs: `"heading" "image" "nameplate"`,
                sm: `"image heading" "image nameplate"`,
              },
            }}
          >
            <Box sx={{ gridArea: "heading" }}>{nodeHeading}</Box>
            <Box sx={{ gridArea: "image" }}>
              <AssetOverviewImage
                assetName={selectedNode.name}
                assetNodeId={selectedNode.id}
                companyKey={companyKey}
                overviewImageDocumentId={selectedNode.overviewImageDocumentId}
              />
            </Box>
            <Stack sx={{ gridArea: "nameplate" }} divider={<Divider sx={{ borderColor: cmmsColors.border }} />}>
              {/* Keyed by node so an open field never carries over to the next node. */}
              <EditableDetailLine
                key={`manufacturer-${selectedNode.id}`}
                label={intl.formatMessage(messages.fieldManufacturer)}
                value={selectedNode.manufacturer}
                {...nameplateLineProps}
                inputProps={nameplateTextInputProps}
                onSave={(manufacturer) => onUpdateNode({ manufacturer })}
              />
              <EditableDetailLine
                key={`model-${selectedNode.id}`}
                label={intl.formatMessage(messages.fieldModel)}
                value={selectedNode.model}
                {...nameplateLineProps}
                inputProps={nameplateTextInputProps}
                onSave={(model) => onUpdateNode({ model })}
              />
              <EditableDetailLine
                key={`modelYear-${selectedNode.id}`}
                label={intl.formatMessage(messages.fieldModelYear)}
                value={selectedNode.modelYear}
                {...nameplateLineProps}
                inputProps={modelYearInputProps}
                // The edit dialog's own parse and range, so the two ways of entering a year agree;
                // the server checks the same range again.
                parse={parseModelYear}
                validate={(year) => (isValidModelYear(year)
                  ? null
                  : intl.formatMessage(messages.toastModelYearInvalid, { minYear: MIN_MODEL_YEAR, maxYear: getMaxModelYear() }))}
                onSave={(modelYear) => onUpdateNode({ modelYear })}
              />
              <EditableDetailLine
                key={`serialNumber-${selectedNode.id}`}
                label={intl.formatMessage(messages.fieldSerialNumber)}
                value={selectedNode.serialNumber}
                {...nameplateLineProps}
                inputProps={nameplateTextInputProps}
                onSave={(serialNumber) => onUpdateNode({ serialNumber })}
              />
            </Stack>
          </Box>
        </Box>
      }

      {!hasMachineCard &&
        <Box sx={getSectionBoxSx(cmmsColors)}>
          {nodeHeading}
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, mt: 1.5 }}>
            <FormattedMessage {...messages.detailsIntro} />
          </Typography>
        </Box>
      }

      <Box sx={getSectionBoxSx(cmmsColors)}>
        <Typography variant="subtitle1" sx={{ color: cmmsColors.text, fontWeight: 700, mb: 1.5 }}>
          <FormattedMessage {...messages.actions} />
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAddChild} disabled={!canAddChild} sx={getContainedButtonSx(cmmsColors)}>
              <FormattedMessage {...messages.addChild} />
            </Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit} sx={getOutlinedButtonSx(cmmsColors)}>
              <FormattedMessage {...cmmsMessages.edit} />
            </Button>
            <Button variant="outlined" startIcon={<DriveFileMoveIcon />} onClick={onMove} disabled={!canMove} sx={getOutlinedButtonSx(cmmsColors)}>
              <FormattedMessage {...messages.move} />
            </Button>
            <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={onDeactivate} disabled={!canDeactivate} sx={getOutlinedButtonSx(cmmsColors, "danger")}>
              <FormattedMessage {...messages.deactivate} />
            </Button>
          </HideIfNoRole>
          {canReport &&
            <HideIfNoRole anyOf={CMMS_FIELD_ROLES}>
              <Button variant="contained" color="warning" startIcon={<ReportProblemIcon />} onClick={() => setReportOpen(true)} sx={getSemanticButtonSx()}>
                <FormattedMessage {...messages.reportProblem} />
              </Button>
            </HideIfNoRole>
          }
        </Stack>
      </Box>

      {/* The strip outside the tile and the tile right under it, so the selected tab joins it. */}
      <Box>
        <Tabs
          value={activeTabIndex}
          onChange={(_event, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={getTabsSx(cmmsColors, { fitLabels: true })}
        >
          {tabs.map((t) => (
            <Tab disableRipple key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={getSectionBoxSx(cmmsColors)}>{activeTab?.render()}</Box>
      </Box>

      <QuickReportDialog
        open={reportOpen}
        assetNodeId={selectedNode.id}
        assetName={selectedNode.name}
        onClose={() => setReportOpen(false)}
      />
    </Stack>
  );
}
